import AppInput from '@/components/ui/AppInput';
import AppPagination from '@/components/ui/AppPagination';
import { endpoints } from '@/services/endpoints';
import { axiosDeleteRequest, axiosGetRequest } from '@/services/Request';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, ArrowLeft, Trash2 } from 'lucide-react';
import { useState, useMemo, useEffect } from 'react';
import { NavLink, useSearchParams } from 'react-router';
import TicketCard from './TicketComponents/TicketCard';
import TicketCardSkeleton from './TicketSkeletons/TicketCardSkeleton';
import BookTicket from './TicketTypes/BookTicket';
import BugTicket from './TicketTypes/BugTicket';
import ChatTicket from './TicketTypes/ChatTicket';
import ReortedUser from './TicketTypes/UserTicket';
import RegularTicket from './TicketTypes/RegularTicket';
import { ITicketData, ticketType } from '@/types';
import ReportActions from './TicketComponents/TicketsActionBar';
import TicketIconLogo from '@/assets/SVGs/icons/TicketLogoIcon';
import AppButton from '@/components/ui/AppButton';
import { toast } from 'react-toastify';
import { TableFilterBar, type FilterConfig } from '@/components/ui/TableFilterBar';

const TICKET_FILTER_CONFIGS: FilterConfig[] = [
  {
    key: 'ticket_status',
    label: 'Status',
    options: [
      { value: '', label: 'All Statuses' },
      { value: 'pending', label: 'Pending' },
      { value: 'in_progress', label: 'In Progress' },
      { value: 'resolved', label: 'Resolved' },
      { value: 'rejected', label: 'Rejected' },
    ],
  },
  {
    key: 'ticket_type',
    label: 'Type',
    options: [
      { value: '', label: 'All Types' },
      { value: 'book', label: 'Book' },
      { value: 'chat', label: 'Chat' },
      { value: 'user', label: 'User' },
      { value: 'bug', label: 'Bug' },
      { value: 'regular', label: 'Regular' },
    ],
  },
  {
    key: 'sort',
    label: 'Sort',
    options: [
      { value: '', label: 'Newest first' },
      { value: 'oldest', label: 'Oldest first' },
    ],
  },
];

const EMPTY_TICKET_FILTERS = { ticket_status: '', ticket_type: '', sort: '' };

export default function Tickets() {
  const [ticketView, setTicketView] = useState({
    type: undefined as ticketType | undefined,
    reporterId: undefined as string | undefined,
    selectedTicket: undefined as ITicketData | undefined,
    isExpanded: false,
    isActive: false,
    selectedTicketId: '',
  });

  const [page, setPage] = useState(1);
  const [filterValues, setFilterValues] = useState<Record<string, string>>(EMPTY_TICKET_FILTERS);
  const [searchParams] = useSearchParams();
  const deepLinkTicketId = searchParams.get('ticketId');

  const [search, setSearch] = useState({
    query: undefined as string | undefined,
    reportCode: undefined as string | undefined,
  });

  const queryClient = useQueryClient();

  const apiParams = Object.fromEntries(
    Object.entries(filterValues).filter(([k, v]) => v !== '' && k !== 'sort')
  );

  const { data: allReports, isLoading } = useQuery({
    queryKey: [endpoints.getAllTickets, page, filterValues],
    queryFn: () =>
      axiosGetRequest(endpoints.getAllTickets, {
        limit: 10,
        page,
        ...apiParams,
      }),
  });

  const { data: SearchedTickets } = useQuery({
    queryKey: [search.query],
    queryFn: () => axiosGetRequest(endpoints.getAllTickets, { ticket_code: search.query }),
    enabled: !!search.query,
  });

  const deleteTicket = useMutation({
    mutationFn: (id: string) => axiosDeleteRequest(endpoints.deleteTicket, { id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [endpoints.getAllTickets] });
      toast.success('Ticket deleted');
      setTicketView({ type: undefined, reporterId: undefined, selectedTicket: undefined, isExpanded: false, isActive: false, selectedTicketId: '' });
    },
    onError: () => toast.error('Failed to delete ticket'),
  });

  // Fetch a specific ticket when navigating from a notification deep link
  const { data: deepLinkTicket } = useQuery({
    queryKey: ['deepLinkTicket', deepLinkTicketId],
    queryFn: () => axiosGetRequest(endpoints.getSpecificTicket, { id: deepLinkTicketId }),
    enabled: !!deepLinkTicketId,
    select: data => data?.result as ITicketData | undefined,
  });

  // Auto-open the deep-linked ticket once it's loaded
  useEffect(() => {
    if (!deepLinkTicket) return;
    const t = deepLinkTicket;
    setTicketView({
      type: t.ticket_type as ticketType,
      reporterId: (t.reporter_user as any)?._id,
      selectedTicket: t,
      isExpanded: true,
      isActive: true,
      selectedTicketId: t._id,
    });
  }, [deepLinkTicket]);

  // Client-side sort by date
  const displayedTickets = useMemo(() => {
    const base = search.query
      ? SearchedTickets?.result ?? []
      : allReports?.result ?? [];
    if (filterValues.sort === 'oldest') {
      return [...base].sort(
        (a, b) => new Date(a.createdAt ?? 0).getTime() - new Date(b.createdAt ?? 0).getTime()
      );
    }
    return base;
  }, [allReports, SearchedTickets, search.query, filterValues.sort]);

  const searchHandler = () => setSearch(prev => ({ ...prev, query: prev.reportCode }));

  const handleEnterKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') searchHandler();
  };

  const handlePageChange = (pageNumber: number) => setPage(pageNumber);

  const handleDisplayTicketType = (type: ticketType, id: string, reportData: ITicketData) => {
    setTicketView({
      type,
      reporterId: id,
      selectedTicket: reportData,
      isExpanded: true,
      isActive: true,
      selectedTicketId: reportData._id,
    });
  };

  function handleFilterChange(key: string, value: string) {
    setFilterValues(prev => ({ ...prev, [key]: value }));
    setPage(1);
  }

  function handleClearAll() {
    setFilterValues(EMPTY_TICKET_FILTERS);
    setPage(1);
  }

  const handleBack = () => {
    setTicketView({
      type: undefined,
      reporterId: undefined,
      selectedTicket: undefined,
      isExpanded: false,
      isActive: false,
      selectedTicketId: '',
    });
  };

  return (
    <div className="flex h-full overflow-hidden rounded-xl border border-coal-200 bg-white shadow-card">
      {/* Ticket list panel */}
      <div className={`flex w-full flex-col border-r border-coal-200 sm:w-80 sm:flex-shrink-0 ${ticketView.isExpanded ? 'hidden sm:flex' : 'flex'}`}>
        {/* Search bar */}
        <div className="flex items-center gap-2 border-b border-coal-200 p-3">
          <AppInput
            icon={Search}
            className="flex-1"
            placeholder="Search by code..."
            disabled={ticketView.isExpanded}
            onChange={e => setSearch(prev => ({ ...prev, reportCode: e.target.value }))}
            onKeyDown={handleEnterKey}
          />
        </div>

        {/* Filter bar */}
        <div className="border-b border-coal-100 px-3 py-2.5">
          <TableFilterBar
            filters={TICKET_FILTER_CONFIGS}
            values={filterValues}
            onChange={handleFilterChange}
            onClearAll={handleClearAll}
          />
        </div>

        {/* Ticket list */}
        <div className="flex flex-1 flex-col justify-between overflow-auto">
          {displayedTickets.length === 0 && !isLoading ? (
            <p className="flex h-full items-center justify-center text-sm text-coal-400">
              No tickets found
            </p>
          ) : (
            <>
              <div className="overflow-auto">
                {isLoading
                  ? Array.from({ length: 10 }).map((_, index: number) => (
                      <TicketCardSkeleton key={index} />
                    ))
                  : displayedTickets.map((ticket: ITicketData) => (
                      <NavLink
                        to=""
                        key={ticket._id}
                        onClick={() =>
                          handleDisplayTicketType(
                            ticket.ticket_type,
                            ticket.reporter_user?.reporter_id,
                            ticket
                          )
                        }
                      >
                        <TicketCard
                          ticket={ticket}
                          isActive={ticketView.selectedTicketId === ticket._id}
                        />
                      </NavLink>
                    ))}
              </div>

              <div className="flex items-center justify-center border-t border-coal-200 py-3">
                <AppPagination
                  isLoading={isLoading}
                  currentPage={allReports?.pagination?.currentPage}
                  totalPages={allReports?.pagination?.totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Ticket detail panel */}
      <div className={`flex w-full flex-col ${ticketView.isExpanded ? 'flex' : 'hidden sm:flex'}`}>
        {ticketView.isExpanded && ticketView.selectedTicket && (
          <div className="flex items-center border-b border-coal-200 bg-white">
            <button
              onClick={handleBack}
              className="flex h-full items-center gap-1 border-r border-coal-200 px-3 py-3 text-sm text-coal-500 transition-colors hover:bg-coal-100 hover:text-coal-700 sm:hidden"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div className="flex-1">
              <ReportActions ticket={ticketView.selectedTicket} />
            </div>
          </div>
        )}
        {ticketView.selectedTicket ? (
          <section className="flex-1 overflow-auto">
            <>
              {ticketView.type === 'chat' && <ChatTicket ticket={ticketView.selectedTicket} />}
              {ticketView.type === 'book' && <BookTicket ticket={ticketView.selectedTicket} />}
              {ticketView.type === 'user' && <ReortedUser ticket={ticketView.selectedTicket} />}
              {ticketView.type === 'bug' && <BugTicket ticket={ticketView.selectedTicket} />}
              {ticketView.type === 'regular' && <RegularTicket ticket={ticketView.selectedTicket} />}
              <div className="flex justify-end border-t border-coal-200 p-3">
                <AppButton
                  theme="error"
                  size="sm"
                  onClick={() => ticketView.selectedTicket && deleteTicket.mutate(ticketView.selectedTicket._id)}
                  loading={deleteTicket.isPending}
                >
                  <Trash2 className="h-4 w-4" />
                  Delete Ticket
                </AppButton>
              </div>
            </>
          </section>
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-4">
            <TicketIconLogo className="w-[15%] min-w-[80px] text-coal-200" />
            <p className="text-sm text-coal-400">Select a ticket to view details</p>
          </div>
        )}
      </div>
    </div>
  );
}
